import { useMemo, useState } from "react"

import { Guid } from "@/types"

export interface IArticleSelectionItem {
    articleId: Guid
    articleDescription: string
    articleNumber: string
    assetsCount: number
}

export interface IArticleSelectionValue {
    selectedArticles: Record<Guid, boolean>
    mergeAssets: Record<Guid, boolean>
    printFeatures: Record<Guid, boolean>
}

export type CommonCheckboxStatus = "empty" | "checked" | "partially"

/** Flags every given article with the same value. */
export function buildAll(articleIds: Guid[], value: boolean): Record<Guid, boolean> {
    return articleIds.reduce<Record<Guid, boolean>>((accum, articleId) => {
        accum[articleId] = value
        return accum
    }, {})
}

/**
 * Tri-state of a bulk checkbox over the given articles.
 *
 * `ids` is the set the status is computed over — for the option columns that is the *selected*
 * articles only, so a deselected article never drags the bulk checkbox into the partial state.
 */
export function getCommonStatus(ids: Guid[], checkedMap: Record<Guid, boolean>): CommonCheckboxStatus {
    if (!ids.length) return "empty"
    const checkedCount = ids.filter(id => checkedMap[id]).length
    switch (checkedCount) {
        case 0: return "empty"
        case ids.length: return "checked"
        default: return "partially"
    }
}

/**
 * Selection state behind the article tables of the configuration-text and per-article-position
 * dialogs.
 *
 * Selection and the two option flags live in three independent maps and the selection never writes
 * into the option maps: a deselected article keeps its "merge assets" / "print features" values, so
 * re-selecting it restores exactly what it had. While an article is deselected its option
 * checkboxes are only masked and disabled.
 *
 * The state is seeded once on mount and deliberately not resynced to later `articles` identities —
 * a re-render of the calling page must not throw away the user's picks. `initialValue` reopens a
 * dialog on a previous selection.
 */
export default function useArticleSelection(
    articles: IArticleSelectionItem[],
    initialValue?: IArticleSelectionValue
) {
    const articleIds = useMemo(() => articles?.map(item => item.articleId) ?? [], [articles])

    const [selectedArticles, setSelectedArticles] = useState<Record<Guid, boolean>>(
        () => initialValue?.selectedArticles ?? buildAll(articleIds, true)
    )
    const [mergeAssets, setMergeAssets] = useState<Record<Guid, boolean>>(
        () => initialValue?.mergeAssets ?? buildAll(articleIds, true)
    )
    const [printFeatures, setPrintFeatures] = useState<Record<Guid, boolean>>(
        () => initialValue?.printFeatures ?? {}
    )

    const selectedArticleIds = useMemo(
        () => articleIds.filter(articleId => selectedArticles[articleId]),
        [articleIds, selectedArticles]
    )
    const hasSelection = selectedArticleIds.length > 0

    const commonSelectionStatus = useMemo(
        () => getCommonStatus(articleIds, selectedArticles),
        [articleIds, selectedArticles]
    )
    const commonMergeAssetsStatus = useMemo(
        () => getCommonStatus(selectedArticleIds, mergeAssets),
        [selectedArticleIds, mergeAssets]
    )
    const commonPrintFeaturesStatus = useMemo(
        () => getCommonStatus(selectedArticleIds, printFeatures),
        [selectedArticleIds, printFeatures]
    )

    const createSelectionClickHandler = (articleId: Guid) => () => {
        setSelectedArticles(prev => ({ ...prev, [articleId]: !prev[articleId] }))
    }

    const handleCommonSelectionCheckboxChange = () => {
        setSelectedArticles(buildAll(articleIds, commonSelectionStatus === "empty"))
    }

    const createOptionClickHandler = (
        articleId: Guid,
        setChecked: typeof setMergeAssets
    ) => () => {
        setChecked(prev => ({ ...prev, [articleId]: !prev[articleId] }))
    }

    const createCommonOptionChangeHandler = (
        status: CommonCheckboxStatus,
        setChecked: typeof setMergeAssets
    ) => () => {
        const nextChecked = status === "empty"
        setChecked(prev => {
            const next = { ...prev }
            selectedArticleIds.forEach(articleId => {
                next[articleId] = nextChecked
            })
            return next
        })
    }

    const value: IArticleSelectionValue = { selectedArticles, mergeAssets, printFeatures }

    return {
        value,
        selectedArticleIds,
        hasSelection,
        commonSelectionStatus,
        commonMergeAssetsStatus,
        commonPrintFeaturesStatus,
        setMergeAssets,
        setPrintFeatures,
        createSelectionClickHandler,
        handleCommonSelectionCheckboxChange,
        createOptionClickHandler,
        createCommonOptionChangeHandler,
    }
}
